import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  LapiError,
  fetchPostSellerAccessUser,
} from '@iwoca/lapi-client/iwocapay';
import { Button } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { useGetSellerAccessUsers } from '../../../../api/lending/lapiHooks';
import CrossIcon from '../../../../assets/CrossIcon.svg';
import TickIcon from '../../../../assets/TickIcon.svg';
import { HookFormDropDown } from '../../../../components/Dropdown/Dropdown';
import { HookFormInput } from '../../../../components/Input/Input';
import { InputError } from '../../../../components/InputError/InputError';
import { useStateKey } from '../../../../hooks/useStateKey.hook';

type AccessUserFormValues = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  accessLevel: string;
};

const ACCESS_USER_INITIAL_VALUES: AccessUserFormValues = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  accessLevel: '',
};

const accessUserValidator: Yup.ObjectSchema<AccessUserFormValues> =
  Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    emailAddress: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required'),
    accessLevel: Yup.string().required('Access level is required'),
  });

const isFeatureFlag = () => {
  return localStorage.viewerUser === 'true';
};

const parseLapiErrorDetail = (err: LapiError): string => {
  try {
    const response = JSON.parse(err.message);
    return response?.errors?.[0]?.detail || 'Unknown error.';
  } catch {
    return err.message || 'Unknown error.';
  }
};

export const AccessUserForm = ({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const { stateKey } = useStateKey();
  const [emailErrors, setEmailErrors] = useState<string | null>(null);
  const { accessUsers } = useGetSellerAccessUsers();

  const methods = useForm<AccessUserFormValues>({
    mode: 'onChange',
    defaultValues: ACCESS_USER_INITIAL_VALUES,
    resolver: yupResolver(accessUserValidator),
  });

  const checkForDuplicateAccessUser = ({
    emailAddress,
  }: {
    emailAddress: string;
  }) => {
    const accessUserEmailList = accessUsers.map((a) => a.email);
    if (accessUserEmailList.includes(emailAddress)) {
      setEmailErrors('This user is already added to this business');
      return;
    }

    setEmailErrors(null);
  };

  if (!stateKey) {
    onCancel();
    return null;
  }

  const onSubmit = methods.handleSubmit(async (values) => {
    try {
      await fetchPostSellerAccessUser({
        stateKey,
        body: {
          email_address: values.emailAddress,
          first_name: values.firstName,
          last_name: values.lastName,
          // @ts-expect-error - string doesn't satisfy lapi
          group_name: values.accessLevel,
        },
      });
      methods.reset();
      onSuccess();
    } catch (err: unknown) {
      if (err instanceof LapiError) {
        setEmailErrors(`Unable to add user: ${parseLapiErrorDetail(err)}`);
      }
    }
  });

  return (
    <div>
      <FormProvider {...methods}>
        <form
          className="flex flex-row justify-around gap-x-l max-600:mb-2xl max-600:mt-3xl max-600:flex-col max-600:gap-y-2xl"
          onSubmit={onSubmit}
        >
          <HookFormInput
            className="flex-[20%]"
            name="firstName"
            label="First name"
            inputMode="text"
          />
          <HookFormInput
            className="flex-[20%]"
            name="lastName"
            label="Last name"
            inputMode="text"
          />
          <div className="flex-[40%]">
            <HookFormInput
              name="emailAddress"
              label="Email address"
              inputMode="text"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                checkForDuplicateAccessUser({
                  emailAddress: e.target.value,
                })
              }
            />
            <InputError isVisible={emailErrors !== null} error={emailErrors} />
          </div>
          <HookFormDropDown
            className="flex-[20%]"
            name="accessLevel"
            labelText="Access level"
            placeholder="Choose an option"
          >
            <option value="iwocapay_seller_admin">Admin</option>
            <option value="iwocapay_seller_editor">Editor</option>
            <option value="iwocapay_seller_contributor">Contributor</option>
            {isFeatureFlag() && (
              <option value="iwocapay_seller_viewer">Viewer</option>
            )}
          </HookFormDropDown>
          <div className="flex flex-row justify-around max-600:inline-grid max-600:grid-cols-2 max-600:gap-[5px]">
            <Button
              type="submit"
              variant="tertiary"
              className="mt-[35px] bg-transparent max-600:mt-s max-600:rounded-s max-600:border max-600:border-secondary-80"
              aria-label="tick-button"
              disabled={
                emailErrors !== null ||
                Boolean(methods.formState.errors.emailAddress) ||
                methods.formState.isSubmitting
              }
            >
              <img src={TickIcon} alt="Tick icon" />
            </Button>
            <Button
              variant="tertiary"
              className="mt-[35px] bg-transparent max-600:mt-s max-600:rounded-s max-600:border max-600:border-secondary-80"
              aria-label="cross-button"
              onClick={onCancel}
            >
              <img src={CrossIcon} alt="Cross icon" />
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
